import { apiClient } from '@/shared/api/client';
import { logger } from '@/shared/utils/logger';
import { extractArrayFromResponse, formatDateToYmd, toSafeString } from "@/shared/utils/apiResponseHelpers";
import type { DeliveryHistoryDetailFilters, DeliverySupplier, DeliveryRider } from '../types/deliveryHistoryDetail.types';
import type {
    DeliveryHistoryDetailResponse,
    DeliverySupplierListResponse,
    DeliverySupplierRawItem,
    DeliveryRiderListResponse,
    DeliveryRiderRawItem,
} from '../types/deliveryHistoryDetail.api.types';

const buildDateParams = (filters: DeliveryHistoryDetailFilters): Record<string, string | undefined> => ({
    suppId: filters.suppId,
    riderId: filters.riderId || undefined,
    fromDate: filters.startDate ? formatDateToYmd(filters.startDate) : undefined,
    toDate: filters.endDate ? formatDateToYmd(filters.endDate) : undefined,
});

const normalizeSupplier = (item: DeliverySupplierRawItem): DeliverySupplier => ({
    suppId: toSafeString(item.suppId),
    suppName: item.suppName ?? '',
});

const normalizeRider = (item: DeliveryRiderRawItem): DeliveryRider => ({
    riderId: toSafeString(item.riderId),
    riderName: item.riderName ?? '',
});

const BASE_PATH = '/api/v1/delivery/history';
const DELIVERY_BASE_PATH = '/api/v1/delivery';
const EXCEL_DOWNLOAD_TIMEOUT = 300000;

export const deliveryHistoryDetailApi = {
    async getDetails(filters: DeliveryHistoryDetailFilters): Promise<DeliveryHistoryDetailResponse> {
        const params = buildDateParams(filters);
        const url = `${BASE_PATH}/fee-list`;
        logger.apiRequest('GET', url, params);
        try {
            const response = await apiClient.get<DeliveryHistoryDetailResponse>(url, { params });
            logger.apiResponse('GET', url, response.status, response.data);
            return response.data;
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },

    async getSuppliers(): Promise<DeliverySupplier[]> {
        const url = `${DELIVERY_BASE_PATH}/supplier/list`;
        logger.apiRequest('GET', url);
        try {
            const response = await apiClient.get<DeliverySupplierListResponse>(url);
            logger.apiResponse('GET', url, response.status, response.data);
            const items = extractArrayFromResponse<DeliverySupplierRawItem>(
                response.data as DeliverySupplierRawItem[] | { suppliers?: DeliverySupplierRawItem[]; items?: DeliverySupplierRawItem[]; data?: DeliverySupplierRawItem[] },
                'suppliers'
            );
            return items.map(normalizeSupplier).filter((item) => item.suppId && item.suppName);
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },

    async getRiders(suppId: string): Promise<DeliveryRider[]> {
        if (!suppId) return [];
        const url = `${DELIVERY_BASE_PATH}/rider/list`;
        const params = { suppId };
        logger.apiRequest('GET', url, params);
        try {
            const response = await apiClient.get<DeliveryRiderListResponse>(url, { params });
            logger.apiResponse('GET', url, response.status, response.data);
            const items = extractArrayFromResponse<DeliveryRiderRawItem>(
                response.data as DeliveryRiderRawItem[] | { riders?: DeliveryRiderRawItem[]; items?: DeliveryRiderRawItem[]; data?: DeliveryRiderRawItem[] },
                'riders'
            );
            return items.map(normalizeRider).filter((item) => item.riderId && item.riderName);
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },

    async downloadExcel(filters: DeliveryHistoryDetailFilters): Promise<Blob> {
        const params = {
            suppId: filters.suppId,
            riderId: filters.riderId || undefined,
            fromDate: filters.startDate ? filters.startDate.replace(/-/g, '') : undefined,
            toDate: filters.endDate ? filters.endDate.replace(/-/g, '') : undefined,
        };
        const url = `${BASE_PATH}/fee-list/excel`;
        logger.apiRequest('GET', url, params);
        try {
            const response = await apiClient.get<Blob>(url, {
                params,
                responseType: 'blob',
                timeout: EXCEL_DOWNLOAD_TIMEOUT,
            });
            logger.apiResponse('GET', url, response.status, 'Blob');
            return response.data;
        } catch (error) {
            logger.apiError('GET', url, error as Error);
            throw error;
        }
    },
};
